/* eslint-disable no-nested-ternary */
import React, { useState } from 'react';
import clsx from 'clsx';
import moment from 'moment';
import { useTheme } from '@material-ui/core/styles';
import FormHelperText from '@material-ui/core/FormHelperText';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Input from '@material-ui/core/Input';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Checkbox from '@material-ui/core/Checkbox';
import ListItemText from '@material-ui/core/ListItemText';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Button from '@material-ui/core/Button';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import TextField from '@material-ui/core/TextField';
import Chip from '@material-ui/core/Chip';
import { Typography } from '@material-ui/core';
import { ActionIcon, ReactDatePicker } from '@mummssoftware/common-ui';
import { getTextEngine } from '@mummssoftware/utils/utils';
import { checkSubqchoices } from '@mummssoftware/utils/form-engine/form-engine-utils';
import { widthMultiplier, isonerequired } from './constants';
import { useStyles } from './style';
import { Text } from './WrapperDiv';
import { checkRequired } from './utils';

type LabelFieldProps = {
  subqchoice?: any;
  text?: string;
  key: string;
  checked?: boolean;
  isExpanded?: boolean;
  submittedState?: Record<string, boolean>;
  disabledState?: Record<string, boolean>;
  required?: boolean;
  onClick: (arg: any) => void;
};
type ReadLabelFieldProps = {
  subqchoice?: any;
  isExpanded?: boolean;
};
type StatementFieldProps = {
  text?: string;
  key: string;
  title?: string;
  subqchoice?: any;
};
type ChipProps = {
  text: string;
};

type InputFieldProps = {
  value: string;
  label: string;
  key: string;
  disabled?: boolean;
  required?: boolean;
  readOnly?: boolean;
  subqchoice?: any;
  handleChange?: (arg: any) => void;
};
type DateFieldProps = {
  value: string | boolean | null;
  label: string;
  key: string;
  disabled?: boolean;
  readOnly?: boolean;
  handleChange?: (arg: any) => void;
};

type FieldProps = {
  checked: boolean;
  radioChecked?: boolean;
  checkboxChecked?: boolean;
  key: string;
  subqchoice: any;
  readOnly?: boolean;
  required?: boolean;
  submittedState?: any;
  disabledState?: any;
  handleChange?: (arg: any) => void;
};
type SelectFieldProps = {
  key: string;
  checked: any;
  subqchoice: any;
  readOnly?: boolean;
  handleChange?: (arg: any) => void;
};

type DCProps = {
  key: string;
  radio?: boolean;
  disabled?: boolean;
  handleChange?: (arg: any) => void;
};

// width of the radio and checkbox elements
const boxWidth = 30;
const expandIconColor = '#355694';

export const LabelField = React.memo(
  ({
    subqchoice,
    onClick,
    isExpanded,
    submittedState,
    required,
    disabledState,
  }: LabelFieldProps) => {
    const theme = useTheme();
    const [expanded, setExpanded] = useState<boolean>(isExpanded || false);
    const classes = useStyles();
    const showIcon = !subqchoice.autounroll && checkSubqchoices(subqchoice);

    let isRequired: any;
    if (required) {
      if (submittedState && submittedState[subqchoice.questionpath]) {
        // if testPassed means all the children have been answered
        const data = required && subqchoice.subqchoices;
        const testPassed = checkRequired(data, submittedState, disabledState);
        isRequired = !testPassed;
      } else {
        isRequired = required;
      }
    }

    const rootClass = isRequired
      ? classes.requiredFieldLabel
      : classes.fieldLabel;
    const iconColor = isRequired ? theme.palette.error.main : expandIconColor;

    const handleClick = () => {
      setExpanded(!expanded);
      onClick(!expanded);
    };

    return (
      <>
        <Text width={subqchoice.width}>
          {showIcon && (
            <ActionIcon
              style={{
                margin: 0,
              }}
              htmlColor={iconColor}
              Icon={expanded ? ExpandMoreIcon : ChevronRightIcon}
              onClick={handleClick}
              value={expanded}
            />
          )}

          <Typography
            variant="body1"
            classes={{
              root: rootClass,
            }}
          >
            {subqchoice.questionlabel}
            {/* {`${subqchoice.questionlabel}-${subqchoice.questionpath}`} */}
          </Typography>
        </Text>
      </>
    );
  },
);

export const StatementField = React.memo(
  ({ subqchoice }: StatementFieldProps) => {
    // css title with bigger font
    const isTitle = subqchoice.cssclass && subqchoice.cssclass === 'title';
    const classes = useStyles();
    const rootClass = isTitle ? classes.statementTitle : classes.fieldLabel;

    return (
      <Text width={subqchoice.width}>
        <Typography
          variant="body1"
          classes={{
            root: rootClass,
          }}
        >
          {subqchoice.questionlabel}
        </Typography>
      </Text>
    );
  },
);

export const ReadOnlyStatementField = React.memo(
  ({ text, title }: StatementFieldProps) => {
    // css title with bigger font
    const isTitle = title && title === 'title';
    const classes = useStyles();
    const rootClass = isTitle
      ? classes.statementReadTitle
      : classes.statementField;

    return (
      <Text>
        <Typography
          variant="body1"
          classes={{
            root: rootClass,
          }}
        >
          {text}
        </Typography>
      </Text>
    );
  },
);

// ReadOnly Form
export const ReadLabelField = React.memo(
  ({ subqchoice, isExpanded }: ReadLabelFieldProps) => {
    const classes = useStyles();

    return (
      <Text>
        <ActionIcon
          style={{
            margin: 0,
          }}
          Icon={isExpanded ? ExpandMoreIcon : ChevronRightIcon}
          id="icon-form"
          htmlColor={expandIconColor}
        />
        <Typography
          variant="body1"
          classes={{
            root: classes.readLabel,
          }}
        >
          {subqchoice.questionlabel}
        </Typography>
      </Text>
    );
  },
);

export const InputField = React.memo(
  ({
    handleChange,
    label,
    disabled,
    value,
    required,
    readOnly,
    subqchoice,
  }: InputFieldProps) => {
    const classes = useStyles();
    const isAddendumStyle = subqchoice.cssclass === 'addendum';
    const labelText = isAddendumStyle ? undefined : label || 'Comment';
    const classField = isAddendumStyle
      ? classes.addendumTextField
      : classes.inputTextField;
    /** format the string */
    const fieldValue = isAddendumStyle ? subqchoice.questionlabel : value;
    const formattedInput =
      typeof fieldValue === 'string' ? getTextEngine(fieldValue) : '';
    const [textValue, setValue] = useState<string>(formattedInput);
    const labelClass = required
      ? classes.requiredInputLabel
      : classes.inputLabel;

    const handleTextChange = (event: any) => {
      setValue(event.target.value);
    };

    const handleOnSave = () => {
      if (!handleChange) {
        return;
      }
      handleChange(textValue);
    };

    return (
      <TextField
        id="text-field-form"
        label={labelText}
        variant="filled"
        multiline
        value={textValue}
        disabled={disabled || readOnly || isAddendumStyle}
        onChange={handleTextChange}
        className={clsx(classes.inputTextField)}
        InputProps={{
          classes: { root: classField },
          disableUnderline: true,
        }}
        onBlur={handleOnSave}
        InputLabelProps={{
          shrink: true,
          classes: { root: labelClass },
        }}
      />
    );
  },
);

export const DateField = React.memo(
  ({ label, value, handleChange, readOnly }: DateFieldProps) => {
    // is Addendum style saving wip issue
    if (value === true) {
      // eslint-disable-next-line no-param-reassign
      value = null;
    }
    const isDateValid = typeof value !== 'boolean' && moment(value).isValid();
    const noDate = value === null;
    const showDate = noDate || isDateValid;
    return showDate ? (
      <>
        <FormHelperText>{label}</FormHelperText>
        <ReactDatePicker
          position="relative"
          zIndex="3"
          selectedDate={(value && moment(value).toDate()) || null}
          onChange={handleChange || (() => null)}
          disabled={readOnly}
        />
      </>
    ) : (
      <></>
    );
  },
);

export const SelectField = React.memo(
  ({ checked, subqchoice, handleChange, readOnly }: SelectFieldProps) => {
    const classes = useStyles();
    const hasValue =
      subqchoice.subqchoices &&
      subqchoice.subqchoices.length &&
      subqchoice.subqchoices[0] !== null;
    let selectValue = '';
    // keys = [] or ['27571->25005->25609']
    const keys = Object.keys(checked);
    if (keys.length) {
      // eslint-disable-next-line prefer-destructuring
      selectValue = keys[0];
    }

    return (
      // if autounroll this is the parent node
      subqchoice.autounroll && (
        <FormControl className={classes.formControl}>
          <InputLabel shrink>{`${subqchoice.questionlabel}`}</InputLabel>
          <Select
            value={hasValue && selectValue}
            onChange={handleChange}
            displayEmpty
            name={subqchoice.questionlabel}
            disabled={readOnly}
          >
            {hasValue &&
              subqchoice.subqchoices.map((choice: any) => (
                <MenuItem
                  key={choice.questionlabel}
                  value={choice.questionpath}
                >
                  {`${choice.questionlabel}`}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      )
    );
  },
);

export const MultiSelectField = React.memo(
  ({ handleChange, subqchoice, checked, readOnly }: SelectFieldProps) => {
    const classes = useStyles();
    const { subqchoices } = subqchoice;
    const hasValue =
      subqchoices && subqchoices.length && subqchoices[0] !== null;
    let selectValue: any = [];
    // checked is {27563->25040->25357->24979->24965: true}
    // keys = [] or ['27571->25005->25609']
    const keys = Object.keys(checked);
    if (keys.length) {
      // eslint-disable-next-line prefer-destructuring
      selectValue = keys;
    }
    const names = hasValue
      ? subqchoices.reduce((seed: any, value: any) => {
          seed[value.questionpath] = value.questionlabel;
          return seed;
        }, {})
      : [];

    const MenuProps = {
      variant: 'menu',
      getContentAnchorEl: null, // to get rid of the jumping.
      // https://stackoverflow.com/questions/59785482/multiselect-box-popover-keeps-jumping-when-scroll-or-select-items
    };

    return (
      // if autounroll this is the parent node
      subqchoice.autounroll && (
        <FormControl className={classes.formControl}>
          <InputLabel shrink>{subqchoice.questionlabel}</InputLabel>
          <Select
            value={selectValue}
            onChange={handleChange}
            displayEmpty
            multiple
            input={<Input />}
            renderValue={(selected: any) => {
              const values = selected.map((el: string) => names[el]);
              return values.join(', ');
            }}
            name={subqchoice.questionlabel}
            MenuProps={MenuProps as any}
            disabled={readOnly}
          >
            {hasValue &&
              subqchoices.map((choice: any) => (
                <MenuItem
                  key={choice.questionlabel}
                  value={choice.questionpath}
                >
                  <Checkbox
                    size="small"
                    checked={!!checked[choice.questionpath]}
                    color="primary"
                    classes={getFieldClasses(classes, readOnly)}
                    name={choice.questionlabel}
                    disableRipple={readOnly}
                  />
                  <ListItemText primary={choice.questionlabel} />
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      )
    );
  },
);

const getFieldClasses = (classes: any, readOnly?: boolean) => {
  if (readOnly) {
    return {
      root: classes.readOnly,
    };
  }
  return {
    root: classes.defaultColor,
  };
};

const getLabelClasses = (
  classes: any,
  readOnly?: boolean,
  required?: boolean,
) => {
  if (readOnly) {
    return {
      label: classes.readOnlyLabel,
      root: classes.readOnlyLabelRoot,
    };
  } else if (required) {
    return {
      label: classes.requiredLabel,
      root: classes.labelRoot,
    };
  }
  return {
    label: classes.label,
    root: classes.labelRoot,
  };
};

const checkElement = (
  required: boolean | undefined,
  isRequiredInitial: boolean | undefined,
  disabledState: Record<string, boolean>,
  subqchoice: any,
  submittedState: Record<string, boolean>,
) => {
  let isRequiredInit = isRequiredInitial;
  if (disabledState && Object.keys(disabledState).length) {
    // disabledState only has one key
    const keys = Object.keys(disabledState).filter(
      (x: string) => disabledState[x],
    );
    if (keys.length) {
      const primaryKey = keys[0];
      if (subqchoice.questionpath.includes(primaryKey)) {
        isRequiredInit = false;
      }
    }
  }

  let isRequired: any;
  if (isRequiredInit) {
    if (submittedState && submittedState[subqchoice.questionpath]) {
      // if testPassed means all the children have been answered
      const data = required && subqchoice.subqchoices;

      const testPassed = checkRequired(data, submittedState, disabledState);
      isRequired = !testPassed;
    } else {
      isRequired = required;
    }
  }
  return isRequired;
};
export const CheckBoxField = React.memo(
  ({
    checked,
    subqchoice,
    handleChange,
    readOnly,
    checkboxChecked,
    required,
    disabledState,
    submittedState,
  }: FieldProps) => {
    const classes = useStyles();
    const isNoRuleRequired = required && !checked;
    const isAtLeastRequired = required && !checkboxChecked;

    const isRequiredInitial =
      subqchoice.rule === isonerequired ? isAtLeastRequired : isNoRuleRequired;
    const isRequired = checkElement(
      required,
      isRequiredInitial,
      disabledState,
      subqchoice,
      submittedState,
    );

    const elWidth = subqchoice.width
      ? subqchoice.width * widthMultiplier + boxWidth
      : undefined;
    const style = subqchoice.width ? { width: `${elWidth}px` } : undefined;
    return (
      <>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={checked}
              onChange={handleChange}
              name={subqchoice.message}
              classes={getFieldClasses(classes, readOnly)}
              disableRipple={readOnly}
              color="primary"
            />
          }
          label={`${subqchoice.questionlabel}`}
          // label={`${subqchoice.questionlabel}-${subqchoice.questionpath}`}
          key={subqchoice.id}
          classes={getLabelClasses(classes, readOnly, isRequired)}
          style={style}
        />
      </>
    );
  },
);

export const RadioField = React.memo(
  ({
    checked,
    subqchoice,
    handleChange,
    readOnly,
    radioChecked,
    required,
    submittedState,
    disabledState,
  }: FieldProps) => {
    const classes = useStyles();
    // isRequired is more complex, depends if one checkbox was selected
    const isRequiredInitial = required && !radioChecked;
    const isRequired = checkElement(
      required,
      isRequiredInitial,
      disabledState,
      subqchoice,
      submittedState,
    );

    const elWidth = subqchoice.width
      ? subqchoice.width * widthMultiplier + boxWidth
      : undefined;
    const style = subqchoice.width ? { width: `${elWidth}px` } : undefined;

    return (
      <>
        <RadioGroup aria-label="gender" name="radio-group" value={checked}>
          <FormControlLabel
            key={subqchoice.questionpath}
            name="radio-cpp"
            // eslint-disable-next-line react/jsx-boolean-value
            value={true}
            control={
              <Radio
                id={subqchoice.questionpath}
                size="small"
                color="primary"
                name="radio-cpp"
                classes={getFieldClasses(classes, readOnly)}
                onClick={handleChange}
                disableRipple={readOnly}
              />
            }
            label={subqchoice.questionlabel}
            // label={`${subqchoice.questionlabel}-${subqchoice.questionpath}`}
            classes={getLabelClasses(classes, readOnly, isRequired)}
            style={style}
          />
        </RadioGroup>
      </>
    );
  },
);

export const DCButton = React.memo(
  ({ handleChange, radio, disabled }: DCProps) => {
    const classes = useStyles();
    const rootClass = radio ? classes.radioButtonRoot : classes.buttonRoot;
    return (
      <Button
        type="submit"
        variant="outlined"
        size="small"
        onClick={handleChange}
        disabled={disabled}
        color="primary"
        classes={{
          label: classes.buttonLabel,
          root: rootClass,
          outlined: classes.buttonOutlined,
        }}
      >
        DC
      </Button>
    );
  },
);
// NOT USED
export const RequiredChip = React.memo(({ text }: ChipProps) => {
  const classes = useStyles();
  return <Chip label={text} classes={{ root: classes.chipRoot }} />;
});
